/**
 * bubu-personaje.js — el acordeón de la banda del personaje (ficha de producto).
 *
 * Una fila abierta a la vez, como mucho. Liquid pinta abierta la primera con contenido,
 * así que al entrar siempre hay algo que leer; a partir de ahí manda quien lee: pulsar
 * la que está abierta la cierra, y se puede dejar la banda entera plegada.
 *
 * La altura se anima a mano (height en píxeles y vuelta a «auto» al acabar) en vez de
 * con max-height: el texto de cada apartado mide lo que mide y con un max-height fijo
 * o se corta el más largo o el más corto tarda una eternidad en abrirse.
 */
const MS = 340;

class BubuAcordeon extends HTMLElement {
  connectedCallback() {
    this.filas = [...this.querySelectorAll('[data-bubu-fila]')];
    this.paneles = [...this.querySelectorAll('[data-bubu-panel]')];
    if (!this.filas.length) return;
    // -1 = todas plegadas. Es un estado legítimo, no un fallo: si Liquid no deja
    // ninguna abierta, no se abre una por nuestra cuenta.
    this.abierta = this.filas.findIndex((f) => f.getAttribute('aria-expanded') === 'true');

    this.addEventListener('click', (e) => {
      const f = e.target.closest('[data-bubu-fila]');
      if (f) this.abre(this.filas.indexOf(f));
    });

    // Flechas arriba/abajo entre cabeceras: el patrón de acordeón de WAI-ARIA.
    this.addEventListener('keydown', (e) => {
      const i = this.filas.findIndex((f) => f === document.activeElement);
      if (i < 0) return;
      let n = null;
      if (e.key === 'ArrowDown') n = (i + 1) % this.filas.length;
      if (e.key === 'ArrowUp') n = (i - 1 + this.filas.length) % this.filas.length;
      if (e.key === 'Home') n = 0;
      if (e.key === 'End') n = this.filas.length - 1;
      if (n === null) return;
      e.preventDefault();
      this.filas[n].focus();
    });
  }

  abre(i) {
    if (i < 0) return;
    // Pulsar la que ya está abierta la cierra. Se pueden quedar las cinco plegadas: la
    // banda no desaparece —siguen los cinco títulos con sus números— y quien no quiera
    // leer nada puede dejarla recogida en vez de tener siempre un apartado desplegado.
    const cierra = i === this.abierta;
    const antes = this.abierta;
    this.abierta = cierra ? -1 : i;
    this.filas.forEach((f, k) => {
      const on = !cierra && k === i;
      f.classList.toggle('is-abierta', on);
      f.setAttribute('aria-expanded', on ? 'true' : 'false');
    });
    if (antes >= 0 && this.paneles[antes]) this.mueve(this.paneles[antes], false);
    if (!cierra && this.paneles[i]) this.mueve(this.paneles[i], true);
  }

  mueve(p, abrir) {
    const quieto = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const alto = () => (p.firstElementChild ? p.firstElementChild.offsetHeight : 0);
    clearTimeout(p._bubuT);
    if (abrir) {
      p.hidden = false;
      if (quieto) {
        p.style.height = 'auto';
        return;
      }
      p.style.height = '0px';
      // Un fotograma de margen: sin él el navegador funde los dos valores en uno y no
      // hay transición que valga.
      requestAnimationFrame(() => {
        p.style.height = alto() + 'px';
        p._bubuT = setTimeout(() => {
          p.style.height = 'auto';
        }, MS);
      });
      return;
    }
    if (quieto) {
      p.style.height = '0px';
      p.hidden = true;
      return;
    }
    p.style.height = alto() + 'px';
    requestAnimationFrame(() => {
      p.style.height = '0px';
      p._bubuT = setTimeout(() => {
        p.hidden = true;
      }, MS);
    });
  }
}

if (!customElements.get('bubu-acordeon')) customElements.define('bubu-acordeon', BubuAcordeon);

export default BubuAcordeon;
